import { ActionNode, ControlNode } from "@/shared/ui/composites";
import { Button, HStack, Icon, VStack } from "@/shared/ui/primitives";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const FeaturesSection = () => {
	const { t } = useTranslation();
	const [activeFeature, setActiveFeature] = useState(0);

	const features = [
		{
			id: 0,
			icon: "Control" as const,
			title: "home.features.design.title",
			description: "home.features.design.description",
		},
		{
			id: 1,
			icon: "Action" as const,
			title: "home.features.execute.title",
			description: "home.features.execute.description",
		},
		{
			id: 2,
			icon: "Robot" as const,
			title: "home.features.autofix.title",
			description: "home.features.autofix.description",
		},
	];

	const renderVisual = (index: number) => {
		if (index === 0) {
			return (
				<VStack className="gap-[12px] items-center md:hidden">
					<ControlNode
						name={t("home.features.design.nodes.input.name")}
						description={t("home.features.design.nodes.input.description")}
					/>
					<Icon name="Down" width={20} height={20} className="text-pm-strong" />
					<ActionNode
						name={t("home.features.design.nodes.analysis.name")}
						description={t("home.features.design.nodes.analysis.description")}
						gyulAmount={3}
					/>
				</VStack>
			);
		}
		if (index === 1) {
			return (
				<VStack className="gap-[12px] items-center md:hidden">
					<ActionNode
						name={t("home.features.execute.nodes.start.name")}
						description={t("home.features.execute.nodes.start.description")}
						gyulAmount={1}
					/>
					<Icon name="Down" width={20} height={20} className="text-pm-strong" />
					<ControlNode
						name={t("home.features.execute.nodes.condition.name")}
						description={t("home.features.execute.nodes.condition.description")}
					/>
					<Icon name="Down" width={20} height={20} className="text-pm-strong" />
					<ActionNode
						name={t("home.features.execute.nodes.run.name")}
						description={t("home.features.execute.nodes.run.description")}
						gyulAmount={2}
					/>
				</VStack>
			);
		}
		return (
			<VStack className="gap-[12px] items-center md:hidden">
				<ActionNode
					name={t("home.features.autofix.nodes.detect.name")}
					description={t("home.features.autofix.nodes.detect.description")}
					gyulAmount={0}
				/>
				<Icon name="Down" width={20} height={20} className="text-pm-strong" />
				<ControlNode
					name={t("home.features.autofix.nodes.analysis.name")}
					description={t("home.features.autofix.nodes.analysis.description")}
				/>
				<Icon name="Down" width={20} height={20} className="text-pm-strong" />
				<ActionNode
					name={t("home.features.autofix.nodes.repair.name")}
					description={t("home.features.autofix.nodes.repair.description")}
					gyulAmount={1}
				/>
			</VStack>
		);
	};

	const renderDesktopVisual = (index: number) => {
		if (index === 0) {
			return (
				<HStack className="gap-[20px] justify-center flex-wrap">
					<ControlNode
						name={t("home.features.design.nodes.input.name")}
						description={t("home.features.design.nodes.input.description")}
					/>
					<Icon name="Action" width={24} height={24} className="text-pm-strong" />
					<ActionNode
						name={t("home.features.design.nodes.analysis.name")}
						description={t("home.features.design.nodes.analysis.description")}
						gyulAmount={3}
					/>
				</HStack>
			);
		}
		if (index === 1) {
			return (
				<HStack className="gap-[20px] justify-center flex-wrap">
					<ActionNode
						name={t("home.features.execute.nodes.start.name")}
						description={t("home.features.execute.nodes.start.description")}
						gyulAmount={1}
					/>
					<ControlNode
						name={t("home.features.execute.nodes.condition.name")}
						description={t("home.features.execute.nodes.condition.description")}
					/>
					<ActionNode
						name={t("home.features.execute.nodes.run.name")}
						description={t("home.features.execute.nodes.run.description")}
						gyulAmount={2}
					/>
				</HStack>
			);
		}
		return (
			<HStack className="gap-[20px] justify-center flex-wrap">
				<ActionNode
					name={t("home.features.autofix.nodes.detect.name")}
					description={t("home.features.autofix.nodes.detect.description")}
					gyulAmount={0}
				/>
				<Icon name="Action" width={24} height={24} className="text-pm-strong" />
				<ControlNode
					name={t("home.features.autofix.nodes.analysis.name")}
					description={t("home.features.autofix.nodes.analysis.description")}
				/>
				<Icon name="Action" width={24} height={24} className="text-pm-strong" />
				<ActionNode
					name={t("home.features.autofix.nodes.repair.name")}
					description={t("home.features.autofix.nodes.repair.description")}
					gyulAmount={1}
				/>
			</HStack>
		);
	};

	return (
		<VStack className="w-full gap-[28px]">
			<h2 className="text-[1.5rem] md:text-[2.25rem] text-ct-strong font-semibold text-center">
				{t("home.features.title")}
			</h2>

			{/* 탭 네비게이션 */}
			<HStack className="gap-[16px] justify-center flex-wrap">
				{features.map((feature, index) => (
					<Button
						key={index}
						variant={activeFeature === index ? "primary" : "default"}
						size="lg"
						onClick={() => setActiveFeature(index)}
						whileHover={{ scale: 1.05 }}
					>
						<HStack className="gap-[10px]">
							<Icon name={feature.icon} width={24} height={24} />
							<span>{t(feature.title)}</span>
						</HStack>
					</Button>
				))}
			</HStack>

			{/* 컨텐츠 영역 */}
			<AnimatePresence mode="wait">
				<motion.div
					key={activeFeature}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -20 }}
					transition={{ duration: 0.3 }}
				>
					<VStack className="gap-[16px] p-[24px] rounded-[16px] bg-bg-regular">
						<p className="text-lg text-ct-regular font-medium text-center">
							{t(features[activeFeature].description)}
						</p>
						<div className="w-full min-h-[200px] hidden md:flex items-center justify-center py-[28px]">
							{renderDesktopVisual(activeFeature)}
						</div>
						<div className="w-full flex items-center justify-center py-[20px] md:hidden">
							{renderVisual(activeFeature)}
						</div>
					</VStack>
				</motion.div>
			</AnimatePresence>
		</VStack>
	);
};
